import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as stylex from "@stylexjs/stylex";
import { queries } from "../lib/api";
import { RequestFailure } from "../components/RequestFailure";
import { isSyncFresh, useHealthClock } from "../lib/health";
import { styles } from "../lib/styles";
import { NavIcon, PageHeader, Stat, EmptyState } from "../components/Workspace";

export const Route = createFileRoute("/")({ component: OverviewPage });
function OverviewPage() {
  const me = useQuery(queries.me());
  const now = useHealthClock();
  const state = useQuery({ ...queries.state(), enabled: me.data?.admin === true });
  if (!me.data?.admin)
    return (
      <>
        <PageHeader
          title="Your access workspace"
          description="Connect your device to securely reach the servers available to you."
        />
        <section data-workspace-panel {...stylex.props(styles.panel)}>
          <EmptyState
            title="Start with your device"
            description="Install SSBD Access, sign in and use your SSH aliases from any terminal or editor."
            action={
              <Link to="/connect" {...stylex.props(styles.primaryLink)}>
                Set up your device
              </Link>
            }
          />
        </section>
      </>
    );
  const data = state.data;
  const applied =
    data?.servers.filter(
      (server) =>
        server.enabled &&
        isSyncFresh(data, now) &&
        data.deployments.some(
          (deployment) => deployment.server_id === server.id && deployment.status === "applied",
        ),
    ).length ?? 0;
  const issues = data?.deployments.filter((deployment) => deployment.error).length ?? 0;
  return (
    <>
      <PageHeader
        title="Overview"
        description="Your server access, operational health and recent changes in one place."
        action={
          <Link to="/servers" {...stylex.props(styles.primaryLink)}>
            View servers
          </Link>
        }
      />
      <RequestFailure
        error={state.error}
        retry={() => {
          void state.refetch();
        }}
        pending={state.isFetching}
      />
      {state.isPending && (
        <p aria-busy="true" {...stylex.props(styles.notice)}>
          Loading workspace…
        </p>
      )}
      {data && (
        <>
          <div className="stat-grid">
            <Stat label="Servers" value={data.servers.length} detail="Registered server accounts" />
            <Stat
              label="Access ready"
              value={applied}
              detail={
                data.last_sync && !isSyncFresh(data, now)
                  ? "Sync is stale; readiness is unverified"
                  : "Enabled with current applied authorization"
              }
              tone="success"
            />
            <Stat
              label="Access rules"
              value={data.rules.filter((rule) => rule.enabled).length}
              detail="Enabled group mappings"
            />
            <Stat
              label="Needs attention"
              value={issues + (!isSyncFresh(data, now) ? 1 : 0)}
              detail="Deployment and synchronization issues"
              tone={issues || !isSyncFresh(data, now) ? "warning" : undefined}
            />
          </div>
          <div className="detail-grid">
            <section data-workspace-panel {...stylex.props(styles.panel)}>
              <div className="section-heading">
                <h2>Operational health</h2>
                <span {...stylex.props(styles.status, isSyncFresh(data, now) && styles.applied)}>
                  {data.sync_error
                    ? "Needs attention"
                    : isSyncFresh(data, now)
                      ? "Synchronized"
                      : data.last_sync
                        ? "Synchronization stale"
                        : "Awaiting sync"}
                </span>
              </div>
              <div className="inline-health">
                <span className={`health-dot${!isSyncFresh(data, now) ? " unhealthy" : ""}`} />
                {data.last_sync
                  ? `Last successful sync ${new Date(data.last_sync).toLocaleString()}`
                  : "No successful synchronization yet"}
              </div>
              {data.sync_error && (
                <p role="alert" {...stylex.props(styles.error)}>
                  {data.sync_error}
                </p>
              )}
              {issues > 0 && (
                <p {...stylex.props(styles.notice)}>
                  {issues} server deployment{issues === 1 ? " needs" : "s need"} attention.{" "}
                  <Link to="/servers">Review servers</Link>
                </p>
              )}
              <div className="link-list">
                <Link to="/rules" search={{ view: "groups" }}>
                  <span>
                    <strong>Manage access</strong>
                    <p {...stylex.props(styles.small)}>Review group rules and individual grants</p>
                  </span>
                  <NavIcon name="ArrowRight" />
                </Link>
                <Link to="/cloud" search={{ view: "inventory" }}>
                  <span>
                    <strong>Cloud inventory</strong>
                    <p {...stylex.props(styles.small)}>Discover and onboard virtual machines</p>
                  </span>
                  <NavIcon name="ArrowRight" />
                </Link>
                <Link to="/recordings">
                  <span>
                    <strong>Review session recordings</strong>
                    <p {...stylex.props(styles.small)}>
                      Encrypted recordings with 30-day retention
                    </p>
                  </span>
                  <NavIcon name="ArrowRight" />
                </Link>
              </div>
            </section>
            <section data-workspace-panel {...stylex.props(styles.panel)}>
              <div className="section-heading">
                <h2>Recent activity</h2>
                <Link to="/audit">View all</Link>
              </div>
              {data.events.length ? (
                data.events.slice(0, 5).map((event) => (
                  <div className="list-row" key={event.id}>
                    <div>
                      <strong>{event.action.replaceAll("_", " ")}</strong>
                      <p {...stylex.props(styles.small)}>{event.actor}</p>
                      <p {...stylex.props(styles.small)}>{event.target}</p>
                    </div>
                    <time {...stylex.props(styles.small)}>
                      {new Date(event.at).toLocaleString()}
                    </time>
                  </div>
                ))
              ) : (
                <EmptyState
                  title="No activity yet"
                  description="Administrative changes and access events will appear here."
                />
              )}
            </section>
          </div>
        </>
      )}
    </>
  );
}
